import React, { useEffect } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { DragItem, DragItemType } from '../schemas';
import { useExerciseDnd } from './DndContext';

// ============================================
// DRAGGABLE WRAPPER
// ============================================

interface DraggableProps {
  id: string | number;
  type: Exclude<DragItem['type'], 'placed-chip'>;
  data: unknown;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Generic draggable wrapper - use for any draggable element
 *
 * @example
 * <Draggable id="opt-1" type="drag-option" data={{ value: '5', label: '5' }}>
 *   <button>5</button>
 * </Draggable>
 */
export const Draggable: React.FC<DraggableProps> = ({
  id,
  type,
  data,
  disabled = false,
  children,
  className = '',
}) => {
  const dragItem: DragItem = { id, type, data };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id,
    data: dragItem,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    cursor: disabled ? 'default' : 'grab',
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`draggable ${isDragging ? 'dragging' : ''} ${className}`}
      {...listeners}
      {...attributes}
    >
      {children}
    </div>
  );
};

// ============================================
// DROPPABLE WRAPPER
// ============================================

interface DroppableProps {
  id: string;
  accepts?: DragItemType[]; // Which types can be dropped here
  onDrop: (item: DragItem) => boolean | void;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Generic droppable wrapper - use for any drop target
 *
 * @example
 * <Droppable id="input-1" accepts={['drag-option']} onDrop={handleDrop}>
 *   <input value={value} readOnly />
 * </Droppable>
 */
export const Droppable: React.FC<DroppableProps> = ({
  id,
  accepts,
  onDrop,
  disabled = false,
  children,
  className = '',
}) => {
  const { activeItem, registerDropHandler, unregisterDropHandler } = useExerciseDnd();

  const {
    setNodeRef,
    isOver,
  } = useDroppable({
    id,
    disabled,
  });

  // Register drop handler
  useEffect(() => {
    registerDropHandler(id, (item: DragItem) => {
      if (accepts && !accepts.includes(item.type)) {
        return false;
      }
      return onDrop(item);
    });

    return () => {
      unregisterDropHandler(id);
    };
  }, [id, accepts, onDrop, registerDropHandler, unregisterDropHandler]);

  // Check if current drag is valid for this target
  const canDrop = activeItem && (!accepts || accepts.includes(activeItem.type));
  const isValidOver = isOver && canDrop;

  return (
    <div
      ref={setNodeRef}
      className={`droppable ${isValidOver ? 'drop-active' : ''} ${isOver && !canDrop ? 'drop-invalid' : ''} ${className}`}
    >
      {children}
    </div>
  );
};
